import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { ChevronDownIcon, PanelRightIcon, XIcon } from 'lucide-react';
import { useMemo } from 'react';

import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { Spinner } from '@/components/ui/spinner';
import { getInitials } from '@/utils/getInitials';

// GraphQL Query for Right Panel data
const GET_ACCOUNT_RIGHT_PANEL = gql`
  query GetAccountRightPanel($id: ID!) {
    node(id: $id) {
      ... on AccountNode {
        id
        name
        website
        relationshipType
        ownerId {
          id
          name
        }
        secondaryOwnerId {
          id
          name
        }
      }
    }
  }
`;

interface AccountRightPanelNode {
  id: string;
  name: string;
  ownerId?: { id: string; name: string } | null;
  relationshipType?: null | string;
  secondaryOwnerId?: { id: string; name: string } | null;
  website?: null | string;
}

interface GetAccountRightPanelData {
  node: AccountRightPanelNode | null;
}

interface GetAccountRightPanelVariables {
  id: string;
}

interface RightPanelData {
  companyName: string;
  owner: string;
  relationshipType: string;
  secondaryOwner: string;
  website: string;
}

export function RightPanel({
  accountId,
  isOpen,
  onClose
}: {
  accountId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { data: apiData, loading } = useQuery<
    GetAccountRightPanelData,
    GetAccountRightPanelVariables
  >(GET_ACCOUNT_RIGHT_PANEL, {
    skip: !accountId || !isOpen,
    variables: { id: accountId }
  });

  const data = useMemo(
    () => mapRightPanelData(apiData?.node ?? null),
    [apiData]
  );

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="sticky top-0 z-10 -mt-16 flex h-[calc(100%+64px)] w-104 flex-col border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="grid h-full place-content-center">
          <Spinner className="size-8 text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-10 flex h-[calc(100dvh-64px)] w-104 flex-col border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {data.companyName || '-'}
            </h3>
            <p className="text-foreground text-sm">Company Name</p>
          </div>
        </div>
        <button
          className="p-1 text-gray-400 hover:text-gray-600"
          onClick={onClose}
          type="button"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>

      {/* More Info Collapsible */}
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800">
          More Info
          <ChevronDownIcon className="h-4 w-4" />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 pb-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-foreground text-sm">Owner</span>
              {data.owner ? (
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-100 text-xs font-medium text-rose-700">
                    {getInitials(data.owner)}
                  </div>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {data.owner}
                  </span>
                </div>
              ) : (
                <span className="text-foreground text-sm">-</span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground text-sm">Website</span>
              {data.website ? (
                <TruncatedTextTooltip className="text-sm text-cyan-600 hover:underline">
                  <a
                    href={
                      data.website.startsWith('http')
                        ? data.website
                        : `https://${data.website}`
                    }
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {data.website}
                  </a>
                </TruncatedTextTooltip>
              ) : (
                <span className="text-foreground text-sm">-</span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground text-sm">Secondary Owner</span>
              {data.secondaryOwner ? (
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700">
                    {getInitials(data.secondaryOwner)}
                  </div>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {data.secondaryOwner}
                  </span>
                </div>
              ) : (
                <span className="text-foreground text-sm">-</span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground text-sm">Relationship Type</span>
              {data.relationshipType ? (
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                  {data.relationshipType}
                </Badge>
              ) : (
                <span className="text-foreground text-sm">-</span>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function mapRightPanelData(node: AccountRightPanelNode | null): RightPanelData {
  if (!node) {
    return {
      companyName: '',
      owner: '',
      relationshipType: '',
      secondaryOwner: '',
      website: ''
    };
  }
  return {
    companyName: node.name,
    owner: node.ownerId?.name ?? '',
    relationshipType: node.relationshipType ?? '',
    secondaryOwner: node.secondaryOwnerId?.name ?? '',
    website: node.website ?? ''
  };
}
export const RightPanelOpenIcon = PanelRightIcon;
